// src/pages/Login.jsx
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import loginBg from "../assets/login.jpg"; // <-- image d'arrière-plan

export default function Login() {
  const nav = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  // Page d'origine (ex: /eleve), sinon /eleve
  const from = location.state?.from?.pathname || "/eleve";

  async function handleGoogle() {
    try {
      setBusy(true);
      setMsg("");

      const provider = new GoogleAuthProvider();
      const { user } = await signInWithPopup(auth, provider);

      // 🔐 Token de session unique pour imposer 1 seule session active
      const sessionToken = uuidv4();

      // Créer/mettre à jour le profil si besoin
      const ref = doc(db, "users", user.uid);
      const snap = await getDoc(ref);

      if (!snap.exists()) {
        // Nouvel utilisateur : élève en attente
        await setDoc(
          ref,
          {
            uid: user.uid,
            email: user.email || null,
            displayName: user.displayName || null,
            role: "eleve",
            access: "pending", // l'admin passera "approved" ensuite
            sessionToken,      // ✅ enregistre le token de session
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } else {
        // Existant : touche légère (ne pas écraser role/access)
        await setDoc(
          ref,
          { sessionToken, updatedAt: serverTimestamp() }, // ✅ rafraîchit le token
          { merge: true }
        );
      }

      // ✅ Sauvegarde locale du token (pour comparaison côté client)
      localStorage.setItem("sessionToken", sessionToken);

      // Redirection : vers la page demandée (souvent /eleve)
      nav(from, { replace: true });
    } catch (e) {
      console.error(e);
      setMsg(e.message || "Échec de la connexion.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/* Arrière-plan avec l'image */}
      <div
        className="login-bg"
        aria-hidden="true"
        style={{ "--login-bg": `url(${loginBg})` }}
      />

      <div className="container py-5 position-relative min-vh-100 d-flex align-items-center justify-content-center">
        <div className="row justify-content-center w-100">
          <div className="col-md-6 col-lg-5">
            <div className="card shadow-sm bg-light bg-opacity-90 border-0">
              <div className="card-body p-4">
                <h1 className="h5 mb-3 text-dark">Connexion</h1>
                <p className="text-dark mb-4">
                  Connecte-toi avec ton compte Google pour accéder à l'espace élève.
                </p>

                <button
                  className="btn btn-primary w-100"
                  onClick={handleGoogle}
                  disabled={busy}
                >
                  {busy ? "Connexion…" : "Se connecter avec Google"}
                </button>

                {msg && <div className="alert alert-danger mt-3 mb-0">{msg}</div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Styles pour l'arrière-plan */}
      <style>{`
        .login-bg {
          position: fixed;
          inset: 0;
          z-index: -1;
          background-image: var(--login-bg);
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          background-attachment: fixed;
          transform: translateZ(0);
        }
        .login-bg::after {
          content: "";
          position: absolute;
          inset: 0;
          /* Voile très léger pour améliorer la lisibilité */
          background: rgba(0, 0, 0, 0.1);
        }
        /* Sur mobile/tablette on évite le 'fixed' */
        @media (max-width: 991.98px) {
          .login-bg { background-attachment: scroll; }
        }
        /* Assurer que le contenu soit centré verticalement */
        .min-vh-100 {
          min-height: 100vh;
        }
      `}</style>
    </>
  );
}
